import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Chip,
  InputAdornment,
} from '@mui/material'
import { Search as SearchIcon, History as HistoryIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { usePermissions } from '../../hooks/usePermissions'

interface NavigationItem {
  text: string
  path: string
  icon: React.ReactNode
  keywords?: string[]
  category?: string
  requiredPermission?: {
    service: string
    resource: string
    action: string
  }
  anyOfPermissions?: Array<{
    service: string
    resource: string
    action: string
  }>
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
  navigationItems: NavigationItem[]
}

const RECENT_PAGES_KEY = 'commandPalette_recentPages'
const MAX_RECENT_PAGES = 5

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose, navigationItems }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentPages, setRecentPages] = useState<string[]>([])
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()

  // Load recent pages from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(RECENT_PAGES_KEY)
    if (stored) {
      try {
        setRecentPages(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to load recent pages:', e)
      }
    }
  }, [])

  // Filter items based on permissions
  const permittedItems = useMemo(() => {
    return navigationItems.filter(item => {
      // If no permission requirement, show to everyone
      if (!item.requiredPermission && !item.anyOfPermissions) {
        return true
      }

      // Check anyOfPermissions first
      if (item.anyOfPermissions && item.anyOfPermissions.length > 0) {
        return item.anyOfPermissions.some(perm =>
          hasPermission(perm.service, perm.resource, perm.action)
        )
      }

      // Check single required permission
      if (item.requiredPermission) {
        return hasPermission(
          item.requiredPermission.service,
          item.requiredPermission.resource,
          item.requiredPermission.action
        )
      }

      return true
    })
  }, [navigationItems, hasPermission])

  // Fuzzy search implementation
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      // When no search query, show recent pages first, then all permitted items.
      // Limit results for performance.
      const recent = recentPages
        .map(path => permittedItems.find(item => item.path === path))
        .filter((item): item is NavigationItem => item !== undefined)

      const recentPaths = new Set(recent.map(r => r.path))
      const others = permittedItems.filter(item => !recentPaths.has(item.path))

      return [...recent, ...others].slice(0, 50)
    }

    const query = searchQuery.toLowerCase()
    const queryParts = query.split(' ').filter(Boolean)

    return permittedItems
      .map(item => {
        let score = 0
        const text = item.text.toLowerCase()
        const path = item.path.toLowerCase()
        const keywords = (item.keywords || []).join(' ').toLowerCase()
        const category = (item.category || '').toLowerCase()
        const searchableText = `${text} ${path} ${keywords} ${category}`

        // Exact match gets highest score
        if (text === query) {
          score = 1000
        } else if (text.startsWith(query)) {
          score = 800
        } else if (text.includes(query)) {
          score = 600
        }

        // Check if all query parts match
        const allPartsMatch = queryParts.every(part =>
          searchableText.includes(part)
        )

        if (allPartsMatch) {
          score += 400
        }

        // Keyword match
        if (keywords.includes(query)) {
          score += 300
        }

        // Category match
        if (category.includes(query)) {
          score += 200
        }

        // Path match
        if (path.includes(query)) {
          score += 100
        }

        return { item, score }
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map(({ item }) => item)
  }, [searchQuery, permittedItems, recentPages])

  // Handle navigation
  const handleNavigate = useCallback((path: string) => {
    // Update recent pages
    const updatedRecent = [
      path,
      ...recentPages.filter(p => p !== path)
    ].slice(0, MAX_RECENT_PAGES)

    setRecentPages(updatedRecent)
    localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(updatedRecent))

    navigate(path)
    onClose()
    setSearchQuery('')
    setSelectedIndex(0)
  }, [navigate, onClose, recentPages])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev =>
            prev < filteredItems.length - 1 ? prev + 1 : prev
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => prev > 0 ? prev - 1 : prev)
          break
        case 'Enter':
          e.preventDefault()
          if (filteredItems[selectedIndex]) {
            handleNavigate(filteredItems[selectedIndex].path)
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          setSearchQuery('')
          setSelectedIndex(0)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, filteredItems, selectedIndex, handleNavigate, onClose])

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      setSelectedIndex(0)
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          top: '12%',
          m: 0,
          borderRadius: 2,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Search pages... (type to search)"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setSelectedIndex(0)
            }}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <Chip
                    label="ESC to close"
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none',
                },
              },
            }}
          />
        </Box>

        <List sx={{ maxHeight: '300px', overflow: 'auto', p: 1 }}>
          {filteredItems.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery
                  ? 'No pages found. Try a different search term.'
                  : 'Start typing to search...'}
              </Typography>
            </Box>
          )}

          {!searchQuery && filteredItems.length > 0 && (
            <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                RECENT
              </Typography>
            </Box>
          )}

          {filteredItems.map((item, index) => (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={index === selectedIndex}
                onClick={() => handleNavigate(item.path)}
                sx={{
                  borderRadius: 1,
                  '&.Mui-selected': {
                    bgcolor: 'primary.light',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  secondary={item.category}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: index === selectedIndex ? 600 : 400,
                  }}
                  secondaryTypographyProps={{
                    fontSize: '0.75rem',
                  }}
                />
                {index === selectedIndex && (
                  <Chip label="↵" size="small" sx={{ fontSize: '0.7rem', ml: 1 }} />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label="↑↓" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
              Navigate
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label="↵" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
              Select
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label="ESC" size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
              Close
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
